import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Download, CreditCard, RefreshCw, Upload, Link2, Copy, Store, ListOrdered } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ExpiryAlertsCard from "../components/ExpiryAlertsCard";
import PrintersCard from "../components/PrintersCard";
import WhatsAppCard from "../components/WhatsAppCard";

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("all");
  const CONF = { all: "AZZERA TUTTO", accounting: "AZZERA CONTABILITA" };
  const [soundOn, setSoundOn] = useState(localStorage.getItem("dg_sound") !== "off");
  const toggleSound = () => { const n = !soundOn; setSoundOn(n); localStorage.setItem("dg_sound", n ? "on" : "off"); };
  const [bk, setBk] = useState(false);
  const [sumup, setSumup] = useState({ configured: false, connected: false, merchant_code: "", last_sync_at: "", redirect_uri: "" });
  const [sumBusy, setSumBusy] = useState(false);
  const loadSumup = async () => { try { const { data } = await api.get("/sumup/status"); setSumup(data); } catch { /* noop */ } };
  useEffect(() => {
    loadSumup();
    const p = new URLSearchParams(window.location.search);
    if (p.get("sumup") === "connected") { toast.success("SumUp collegato con successo"); window.history.replaceState({}, "", window.location.pathname); }
    else if (p.get("sumup") === "error") { toast.error("Collegamento SumUp non riuscito"); window.history.replaceState({}, "", window.location.pathname); }
  }, []);
  const connectSumup = async () => {
    try { const { data } = await api.get("/sumup/connect"); window.location.href = data.authorization_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const syncSumup = async () => {
    setSumBusy(true);
    try { const { data } = await api.post("/sumup/sync"); toast.success(`Sincronizzato: ${data.imported} nuovi incassi · ${data.updated} aggiornati`); loadSumup(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSumBusy(false); }
  };
  const disconnectSumup = async () => {
    if (!window.confirm("Scollegare il conto SumUp?")) return;
    try { await api.post("/sumup/disconnect"); toast.success("SumUp scollegato"); loadSumup(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [ingest, setIngest] = useState({ token: "", last_ingest: null });
  const loadIngest = async () => { try { const { data } = await api.get("/settings/cash-ingest-token"); setIngest(data); } catch { /* noop */ } };
  useEffect(() => { loadIngest(); }, []);
  const ingestUrl = `${process.env.REACT_APP_BACKEND_URL}/api/ingest/easyevolver?token=${ingest.token || ""}`;
  const regenIngest = async () => {
    if (!window.confirm("Rigenerare il token? Il vecchio comando sul PC cassa smetterà di funzionare finché non aggiorni l'URL.")) return;
    try { const { data } = await api.post("/settings/cash-ingest-token/regenerate"); setIngest((s) => ({ ...s, token: data.token })); toast.success("Token rigenerato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyText = async (t, msg) => { try { await navigator.clipboard.writeText(t); toast.success(msg || "Copiato"); } catch { toast.error("Copia non riuscita"); } };
  const curlCmd = `curl -s -F "file=@%DBPATH%\\ExportVenduto.csv" "${ingestUrl}"`;

  const fileRef = useRef(null);
  const [restoring, setRestoring] = useState(false);
  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!window.confirm("Ripristinare i dati da questo file di backup?\n\nI dati vengono aggiunti/aggiornati (gli utenti e le credenziali NON vengono toccati). Usalo per trasferire i dati dall'anteprima alla produzione.")) return;
    setRestoring(true);
    try {
      const json = JSON.parse(await file.text());
      const { data } = await api.post("/admin/restore", { collections: json.collections || json });
      const tot = Object.values(data.restored || {}).reduce((s, n) => s + n, 0);
      toast.success(`Ripristino completato: ${tot} record importati`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "File di backup non valido"); }
    finally { setRestoring(false); }
  };

  const close = () => { setStep(0); setText(""); setMode("all"); };

  const downloadBackup = async () => {
    setBk(true);
    try {
      const { data } = await api.get("/admin/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `backup-dogana-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBk(false); }
  };

  if (user?.role !== "admin") {
    return (
      <div>
        <PageHeader title="Impostazioni" subtitle="Accesso riservato all'amministratore" testid="settings-header" />
        <Card className="p-6 rounded-xl max-w-2xl" data-testid="settings-forbidden">
          <p className="text-sm text-muted-foreground">Solo l'amministratore può accedere a questa sezione.</p>
        </Card>
      </div>
    );
  }

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-data", { scope: mode, confirm: CONF[mode] });
      const tot = Object.values(data.deleted || {}).reduce((s, n) => s + (Number(n) || 0), 0);
      toast.success(`${mode === "accounting" ? "Contabilità azzerata" : "Dati azzerati"}: ${tot} record`);
      close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const startReset = (m) => { setMode(m); setStep(1); };

  return (
    <div>
      <PageHeader title="Impostazioni" subtitle="Gestione avanzata (solo amministratore)" testid="settings-header" />

      <Card className="p-6 rounded-xl max-w-2xl mb-6 flex items-center justify-between gap-4" data-testid="sound-settings">
        <div>
          <p className="font-medium">Suono nuovi ordini</p>
          <p className="text-sm text-muted-foreground">Beep discreto sui monitor Bar/Cucina e avviso "tavolo pronto" al cameriere.</p>
        </div>
        <Button variant={soundOn ? "default" : "outline"} className="rounded-full" onClick={toggleSound} data-testid="sound-toggle-button">
          {soundOn ? "Attivo" : "Disattivato"}
        </Button>
      </Card>

      <PrintersCard />

      <WhatsAppCard />

      <Card className="p-6 rounded-xl max-w-2xl mb-6" data-testid="sumup-card">
        <div className="flex items-center gap-2 mb-2 text-primary"><CreditCard className="w-5 h-5" /><h3 className="font-dash font-semibold text-lg">Conto SumUp</h3></div>
        {!sumup.configured ? (
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Per collegare SumUp servono <b>Client ID</b> e <b>Client Secret</b> di un'app creata su developer.sumup.com. Registra questo URL di reindirizzamento nell'app SumUp:</p>
            <code className="block text-xs bg-secondary/60 rounded-lg p-2 break-all" data-testid="sumup-redirect-uri">{sumup.redirect_uri || "(configura SUMUP_REDIRECT_URI)"}</code>
            <p>Poi comunica le credenziali per completare la configurazione.</p>
          </div>
        ) : sumup.connected ? (
          <div className="space-y-3">
            <p className="text-sm text-[hsl(var(--success))]" data-testid="sumup-connected">Collegato{sumup.merchant_code ? ` · negozio ${sumup.merchant_code}` : ""}. Gli incassi SumUp vengono importati come <b>incassi ufficiali</b> e sincronizzati automaticamente ogni giorno.</p>
            {sumup.last_sync_at && <p className="text-xs text-muted-foreground">Ultima sincronizzazione: {new Date(sumup.last_sync_at).toLocaleString("it-IT")}</p>}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full" onClick={syncSumup} disabled={sumBusy} data-testid="sumup-sync-button"><RefreshCw className={`w-4 h-4 mr-2 ${sumBusy ? "animate-spin" : ""}`} />{sumBusy ? "Sincronizzo…" : "Sincronizza ora"}</Button>
              <Button variant="outline" className="rounded-full" onClick={() => navigate("/contabilita/registro/sumup")} data-testid="sumup-register-button"><ListOrdered className="w-4 h-4 mr-2" /> Vedi movimenti</Button>
              <Button variant="ghost" className="rounded-full text-destructive" onClick={disconnectSumup} data-testid="sumup-disconnect-button">Scollega</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Collega il tuo conto SumUp per importare automaticamente gli incassi con carta.</p>
            <Button className="rounded-full" onClick={connectSumup} data-testid="sumup-connect-button"><CreditCard className="w-4 h-4 mr-2" /> Collega SumUp</Button>
          </div>
        )}
      </Card>

      <ExpiryAlertsCard />

      <Card className="p-6 rounded-xl max-w-2xl mb-6" data-testid="cash-ingest-card">
        <div className="flex items-center gap-2 mb-2 text-primary"><Store className="w-5 h-5" /><h3 className="font-dash font-semibold text-lg">Collega la cassa (EasyEvolver)</h3></div>
        <p className="text-sm text-muted-foreground mb-4">
          Configura EasyEvolver perché, a fine giornata, invii da solo il file del venduto (ExportVenduto CSV/XML).
          Gli incassi finiscono in <b>Contabilità</b> (incasso giornaliero ufficiale) e il <b>venduto per prodotto</b> viene tracciato per il Menu Engineering. I doppioni sono ignorati.
        </p>

        <Label className="text-xs uppercase text-muted-foreground">URL di invio (con token segreto)</Label>
        <div className="flex items-center gap-2 mt-1 mb-4">
          <code className="flex-1 text-xs bg-secondary/60 rounded-lg p-2 break-all" data-testid="ingest-url">{ingest.token ? ingestUrl : "(genero il token…)"}</code>
          <Button size="icon" variant="outline" className="rounded-full shrink-0" onClick={() => copyText(ingestUrl, "URL copiato")} data-testid="copy-ingest-url"><Copy className="w-4 h-4" /></Button>
        </div>

        <Label className="text-xs uppercase text-muted-foreground">Comando per il "Batch applicazione" di EasyEvolver</Label>
        <div className="flex items-center gap-2 mt-1 mb-2">
          <code className="flex-1 text-xs bg-secondary/60 rounded-lg p-2 break-all" data-testid="ingest-curl">{curlCmd}</code>
          <Button size="icon" variant="outline" className="rounded-full shrink-0" onClick={() => copyText(curlCmd, "Comando copiato")} data-testid="copy-ingest-curl"><Copy className="w-4 h-4" /></Button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          In EasyEvolver: <b>Tracciati export → Codici Movimento</b> esporta il venduto in un file CSV, poi in <b>Batch applicazione</b> incolla questo comando (adatta il percorso del file). Serve <code>curl</code> e connessione internet sul PC cassa.
        </p>

        {ingest.last_ingest && (
          <p className="text-xs text-[hsl(var(--success))] mb-4" data-testid="ingest-last">
            Ultimo invio: {new Date(ingest.last_ingest.at).toLocaleString("it-IT")} · {ingest.last_ingest.income_imported} incassi · {ingest.last_ingest.sales_imported} righe prodotto
          </p>
        )}

        <Button variant="ghost" className="rounded-full text-destructive" onClick={regenIngest} data-testid="regen-ingest-token"><RefreshCw className="w-4 h-4 mr-2" /> Rigenera token</Button>
      </Card>

      <Card className="p-6 rounded-xl max-w-2xl mb-6" data-testid="backup-card">
        <div className="flex items-center gap-2 mb-2 text-primary">
          <Download className="w-5 h-5" />
          <h3 className="font-dash font-semibold text-lg">Backup dei dati</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Scarica un file (JSON) con tutti i tuoi dati: contabilità, magazzino, lotti, HACCP,
          prenotazioni, clienti, fornitori, menù e impostazioni. Consigliato <b>prima di azzerare</b>.
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          Scarica un file (JSON) con tutti i tuoi dati: contabilità, magazzino, lotti, HACCP,
          prenotazioni, clienti, fornitori, menù e impostazioni. Consigliato <b>prima di azzerare</b>.
          Puoi anche <b>ripristinare</b> un backup: utile per trasferire i dati dall'anteprima alla versione pubblicata.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={downloadBackup} disabled={bk} data-testid="download-backup-button">
            <Download className="w-4 h-4 mr-2" /> {bk ? "Preparo il backup…" : "Scarica backup"}
          </Button>
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={restoreBackup} data-testid="restore-file-input" />
          <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={restoring} data-testid="restore-backup-button">
            <Upload className="w-4 h-4 mr-2" /> {restoring ? "Ripristino…" : "Ripristina da backup"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 rounded-xl border-destructive/40 max-w-2xl" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-2 text-destructive">
          <AlertTriangle className="w-5 h-5" />
          <h3 className="font-dash font-semibold text-lg">Zona pericolosa</h3>
        </div>
        <div className="rounded-lg border border-border p-4 mb-4">
          <p className="text-sm font-medium mb-1">Azzera solo la contabilità</p>
          <p className="text-sm text-muted-foreground mb-3">
            Cancella <b>incassi, spese, giroconti, chiusure cassa, fatture/documenti e venduto</b> e azzera i <b>debiti fornitori</b>.
            Restano intatti <b>magazzino, lotti/tracciabilità, HACCP, menù, clienti, fornitori e ricette</b>. Irreversibile.
          </p>
          <Button variant="outline" className="rounded-full border-destructive/60 text-destructive hover:bg-destructive/10" onClick={() => startReset("accounting")} data-testid="reset-accounting-button">
            <Trash2 className="w-4 h-4 mr-2" /> Azzera solo la contabilità
          </Button>
        </div>
        <div className="rounded-lg border border-destructive/30 p-4">
          <p className="text-sm font-medium mb-1">Azzera tutti i dati</p>
          <p className="text-sm text-muted-foreground mb-3">
            Azzera <b>tutti i dati operativi</b>: contabilità, magazzino, lotti, HACCP, prenotazioni,
            clienti, fornitori e documenti. Restano intatti menù, utenti e impostazioni. Irreversibile.
          </p>
          <Button variant="destructive" className="rounded-full" onClick={() => startReset("all")} data-testid="reset-data-button">
            <Trash2 className="w-4 h-4 mr-2" /> Azzera tutti i dati
          </Button>
        </div>
      </Card>
      {/* Conferma 1 */}
      <Dialog open={step === 1} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="reset-confirm-1">
          <DialogHeader><DialogTitle>Conferma 1 di 3{mode === "accounting" ? " · Solo contabilità" : ""}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{mode === "accounting" ? "Stai per azzerare SOLO la contabilità (incassi, spese, giroconti, chiusure cassa, fatture/documenti, venduto e debiti fornitori). Magazzino, lotti, HACCP, menù, clienti e ricette restano intatti. Vuoi continuare?" : "Stai per cancellare tutti i dati operativi. Vuoi continuare?"}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={close} data-testid="reset-cancel-1">Annulla</Button>
            <Button variant="destructive" className="rounded-full" onClick={() => setStep(2)} data-testid="reset-next-1">Continua</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma 2 */}
      <Dialog open={step === 2} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="reset-confirm-2">
          <DialogHeader><DialogTitle>Conferma 2 di 3</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Questa azione è <b>irreversibile</b>: i dati NON potranno essere recuperati. Sei davvero sicuro?</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={close} data-testid="reset-cancel-2">Annulla</Button>
            <Button variant="destructive" className="rounded-full" onClick={() => setStep(3)} data-testid="reset-next-2">Sì, sono sicuro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma 3 */}
      <Dialog open={step === 3} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="reset-confirm-3">
          <DialogHeader><DialogTitle>Conferma 3 di 3</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">Per procedere, scrivi <b>{CONF[mode]}</b> qui sotto:</p>
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={CONF[mode]} data-testid="reset-confirm-input" />
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={close} data-testid="reset-cancel-3">Annulla</Button>
            <Button variant="destructive" className="rounded-full" disabled={text !== CONF[mode] || busy} onClick={doReset} data-testid="reset-final-button">
              {busy ? "Azzero…" : "Azzera definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
